import React from 'react';
import {
    Button,
    Drawer,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerBody,
    DrawerFooter,
    Field,
    Input,
    Spinner,
    MessageBar,
    MessageBarBody,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { ICartBundle } from '../../Api/Models/Cart';
import { useGetBundleQuery, useUpdateCartBundleMutation } from '../../Api/Api';
import { useUpdatePreparedCartBundleMutation } from '../../Api/PreparedCartEndpoints';
import { useBundleConfiguration, stripNonNumeric } from '../Bundle/useBundleConfiguration';
import BundleOptionsTables from '../Bundle/BundleOptionsTables';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import { useLocalization } from '../Helpers/useLocalization';
import CAN, { PRICING } from '../Auth/Permissions';

interface IEditBundleDrawerProps {
    // The cart bundle line being edited, or null when the panel is closed.
    cartBundle: ICartBundle | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // 'cart' (default) saves to the regular cart; 'prepared' saves to a prepared cart.
    mode?: 'cart' | 'prepared';
    // Required when mode === 'prepared'.
    preparedCartId?: string;
    // The workspace item id of the bundle on the prepared cart (required for prepared mode).
    workspaceItemId?: string;
    // Product ids that are rep-locked and cannot be deselected (prepared cart, rep-configured bundles).
    lockedProductIds?: string[];
}

/**
 * Slide-out panel for re-configuring a bundle already in the cart. Lays out like the bundle page
 * (via the shared BundleOptionsTables + useBundleConfiguration), seeded with the cart line's current
 * selections, and saves the new selection back to the same cart line through updateCartBundle (regular
 * cart) or updatePreparedCartBundle (prepared cart). Pass lockedProductIds to lock the rep's
 * pre-done selections while keeping quantities and unselected optionals editable.
 */
const EditBundleDrawer: React.FC<IEditBundleDrawerProps> = (props) => {
    return (
        <Drawer
            position='end'
            size='large'
            open={props.open}
            onOpenChange={(_, { open }) => props.onOpenChange(open)}
        >
            {/* Keyed on the cart line so each open remounts and re-seeds from the current selection. */}
            {props.open && props.cartBundle && (
                <EditBundleDrawerContent
                    key={props.cartBundle.cartItemId ?? props.cartBundle.bundleId}
                    cartBundle={props.cartBundle}
                    onClose={() => props.onOpenChange(false)}
                    mode={props.mode ?? 'cart'}
                    preparedCartId={props.preparedCartId}
                    workspaceItemId={props.workspaceItemId}
                    lockedProductIds={props.lockedProductIds}
                />
            )}
        </Drawer>
    );
};

interface IEditBundleDrawerContentProps {
    cartBundle: ICartBundle;
    onClose: () => void;
    mode: 'cart' | 'prepared';
    preparedCartId?: string;
    workspaceItemId?: string;
    lockedProductIds?: string[];
}

const EditBundleDrawerContent: React.FC<IEditBundleDrawerContentProps> = ({ cartBundle, onClose, mode, preparedCartId, workspaceItemId, lockedProductIds }) => {
    const L = useLocalization();
    const canSeePricing = CAN(PRICING);
    const bundle = useGetBundleQuery({ bundleId: cartBundle.bundleId });
    const [updateCartBundle, { isLoading: savingCart }] = useUpdateCartBundleMutation();
    const [updatePreparedCartBundle, { isLoading: savingPrepared }] = useUpdatePreparedCartBundleMutation();
    const saving = savingCart || savingPrepared;
    const [error, setError] = React.useState<string | undefined>();
    const [quantity, setQuantity] = React.useState<number>(cartBundle.quantity);
    const [tmpQuantity, setTmpQuantity] = React.useState<string>(cartBundle.quantity.toString());
    const [warningMessage, setWarningMessage] = React.useState<string>('');

    React.useEffect(() => { setTmpQuantity(quantity.toString()); }, [quantity]);

    const trySetQuantity = (qty: number) => {
        if (qty < 1) {
            setWarningMessage('Quantity must be at least 1');
            setQuantity(1);
        } else if (bundle.data?.maxQuantity && qty > bundle.data.maxQuantity) {
            setWarningMessage(`Only ${bundle.data.maxQuantity} available for purchase`);
            setQuantity(bundle.data.maxQuantity);
        } else {
            setWarningMessage('');
            setQuantity(qty);
        }
    };

    const trySetTmpQuantity = (value: string) => {
        const numericValue = stripNonNumeric(value);
        if (numericValue === '') { setWarningMessage('Quantity cannot be empty'); setTmpQuantity(''); return; }
        const parsed = parseInt(numericValue);
        if (isNaN(parsed)) { setWarningMessage('Quantity must be a number'); return; }
        if (parsed < 1) { setWarningMessage('Quantity must be a number greater than 0'); setTmpQuantity('1'); }
        else { setWarningMessage(''); setTmpQuantity(numericValue); }
    };

    const blurQuantity = () => {
        const parsed = parseInt(tmpQuantity);
        trySetQuantity(isNaN(parsed) ? 1 : parsed < 1 ? 1 : parsed);
    };

    // Seed the configuration from the cart line's current selection (group membership is derived
    // from the bundle definition inside the hook, since cart products carry no groupId).
    const initialProducts = React.useMemo(
        () => cartBundle.products?.map(p => ({ productId: p.productId, quantity: p.quantity })) ?? [],
        [cartBundle.products]
    );
    // Rep-preconfigured items cannot be reduced below their locked quantity. preparedConfiguration
    // carries each locked item's { productId, quantity }; empty for regular-cart edits (floor stays 1).
    const minItemQuantities = React.useMemo(() => Object.fromEntries(
        (cartBundle.preparedConfiguration ?? []).map(s => [s.productId, s.quantity])
    ), [cartBundle.preparedConfiguration]);
    const config = useBundleConfiguration(bundle.data, { initialProducts, bundleQuantity: quantity, lockedProductIds, minItemQuantities });

    const save = async () => {
        setError(undefined);
        if (mode === 'prepared') {
            if (!preparedCartId || !workspaceItemId) {
                setError('Missing prepared cart context. Please close and try again.');
                return;
            }
            const result = await updatePreparedCartBundle({
                cartId: preparedCartId,
                wsItemId: workspaceItemId,
                quantity: quantity < 1 ? 1 : quantity,
                selection: config.bundleSelection,
            });
            if ('error' in result || !result.data?.success) {
                setError(('error' in result ? undefined : result.data?.message) ?? 'Could not save changes. Please try again.');
                return;
            }
        } else {
            const result = await updateCartBundle({
                cartItemId: cartBundle.cartItemId ?? cartBundle.bundleId,
                quantity: quantity < 1 ? 1 : quantity,
                selection: config.bundleSelection,
            });
            if ('error' in result || !result.data?.success) {
                setError(('error' in result ? undefined : result.data?.error ?? result.data?.message) ?? 'Could not save changes. Please try again.');
                return;
            }
        }
        onClose();
    };

    return (
        <>
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance='subtle'
                            aria-label={L('localization_common_close')}
                            icon={<Dismiss24Regular />}
                            onClick={onClose}
                        />
                    }
                >
                    {L('localization_cart_edit_bundle_title')}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                {(bundle.isLoading || bundle.isFetching) && <Spinner label={L('localization_common_loading')} />}
                {bundle.data && (
                    <>
                        <h3 style={{ lineHeight: 1.3, marginTop: 0 }}>{bundle.data.name}</h3>
                        <Field style={{ maxWidth: '200px', marginBottom: '1em', marginTop: '1em' }}>
                            <Input
                                contentBefore={<>Quantity: </>}
                                style={{ width: '100%', textAlign: 'right' }}
                                value={tmpQuantity}
                                onChange={(_, d) => trySetTmpQuantity(d.value)}
                                onBlur={blurQuantity}
                            />
                        </Field>
                        {warningMessage && <MessageBar intent='warning' style={{ marginBottom: '1em' }}><MessageBarBody>{warningMessage}</MessageBarBody></MessageBar>}
                        <BundleOptionsTables
                            bundle={bundle.data}
                            config={config}
                            canSeePricing={canSeePricing}
                            hideBundleTotal
                        />
                        {error && <MessageBar intent='error' style={{ marginTop: '1em' }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
                    </>
                )}
            </DrawerBody>
            <DrawerFooter style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5em' }}>
                {canSeePricing && (config.estimatedPrice !== null || config.isEstimating) && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '0.75em 1em', borderTop: '2px solid #e0e0e0', fontWeight: 'bold', fontSize: '1.05em' }}>
                        <span style={{ marginRight: '1.5em' }}>Bundle Total:</span>
                        {config.isEstimating
                            ? <Spinner size='tiny' />
                            : config.estimatedPrice !== null && <CurrencyFormat value={config.estimatedPrice} />}
                    </div>
                )}
                <div style={{ display: 'flex', gap: '0.5em' }}>
                    <Button
                        appearance='primary'
                        onClick={save}
                        disabled={saving || !bundle.data}
                        icon={saving ? <Spinner size='tiny' /> : undefined}
                    >
                        {L('localization_common_save')}
                    </Button>
                    <Button appearance='secondary' onClick={onClose} disabled={saving}>
                        {L('localization_common_cancel')}
                    </Button>
                </div>
            </DrawerFooter>
        </>
    );
};

export default EditBundleDrawer;
